<template>
  <div class="contacts-table-wrap">

    <ContactModal @created="onContactCreated" @updated="onContactUpdated" @deleted="removeContactFromList" :show-modal-on="['EditContact', 'AddContact']"></ContactModal>
    <ProjectModal @created="onProjectCreated" @updated="onProjectUpdated" @deleted="deleteProject" :show-modal-on="['EditProject', 'AddProject']"></ProjectModal>

    <FilterBar
        cookieName="ContactFilterOptionsVue"
        :mainFilters="['SearchFilter', 'ContactSorting']"
        :secondaryFilters="['ContactTypeFilter', 'ContactActiveFilter']"
        @change="filtersChanged"
        filterBarType="contacts"
    >
      <template v-slot:right-bar>
        <TotalAmountContact num-format="'int'" />
      </template>
    </FilterBar>

    <div class="content--inner">
      <b-table
          :fields="contactTableFields"
          :items="contactTableItems"
          :key="contactTableKey"
          :class="['gk-table gk-table--responsive gk-table--striped']"
          @row-clicked="contactRowClicked"
          :busy="loading"
          show-empty
          table-busy
      >
        <template #empty="">
          <EmptyState>
            <template v-slot:image>
              <img src="/static/img/empty-states/empty-state-cost.png">
            </template>
            <template v-slot:title>
              {{ $gettext('We couldn\'t find any contacts') }}
            </template>
            <template v-slot:content>
              {{ $gettext('Start by creating a new one or try updating your filters.') }}
            </template>
            <template v-slot:cta>
              <GekkoButton
                  :text="$gettext('Add a new contact')"
                  icon="fa-solid fa-plus"
                  :to="{ name: 'AddContact' }"
                  class="mr-2"
              />
            </template>
          </EmptyState>
        </template>
        <template #table-busy="">
          <LoadingSpinner min-height />
        </template>
        <template #cell(name)="data">
          <div class="av-name-cont">
            <div class="avatar-col pr-0"><b-avatar class="avatar-color" :text="data.item.contact_name[0]"></b-avatar></div>
            <div class="pl-0" style="display: inline-block">
              <div class="text-ellipsis">{{data.item.name}}</div>
              <div @click.stop style="display: inline-block">
                <GekkoButton
                    @click="changeProjectsArrowState(data)"
                    @stop="contactRowClicked"
                    :text="data.item.projects.length + ' ' + $gettext('projects')"
                    :icon="getProjectsArrowIcon(data.item)"
                    :icon-right="true"
                    class="text-info px-0"
                    variant="link"
                />
              </div>
            </div>
          </div>
        </template>

        <template #cell(details)="data">
          <div>{{data.item.email}}</div>
          <div class="secondary-text">{{data.item.phone_number}}</div>
        </template>

        <template #cell(full_address)="data">
          <div>{{data.item.full_address}}</div>
          <div class="secondary-text">{{data.item.country_name}}</div>
        </template>

        <template #cell(label)="data">
          <div class="contact-type-cell">
            <img v-if="data.item.created_via=='woocommerce'" src="/static/img/woocommerce2.png" width="18px" height="18px" style="margin-right:6px;" />
            <b-badge :class="['status-badge', getContactTypeClass(data.item)]">{{labelText(data.item)}}</b-badge>
          </div>
        </template>

        <template #row-details="row">
          <div>

            <GekkoButton
                @click="goToAddProject(row)"
                :text="$gettext('New project')"
                icon="fa-solid fa-plus"
                class="new-project-button text-info px-0"
                variant="link"
            />

            <b-table
                v-if="row.item.projects.length"
                :class="['gk-table gk-table--striped']"
                :fields="projectTableFields"
                :items="row.item.projects"
                tbody-tr-class="project-row-class"
                :key="projectTableKey"
                @row-clicked="projectRowClicked"
                class="project-table"
            >
              <template #cell(actions)="data">
                <div class="float-right">

                  <ProjectArchive :data_object="data.item" @stop="projectRowClicked" class="project-actions" />

                  <div @click.stop style="display: inline-block" class="project-actions">
                    <GekkoButton
                        @click="markAsFavorite(data.item)"
                        @stop="projectRowClicked"
                        :icon="favoriteIcon(data.item)"
                        variant="link"
                        class="text-favorite"
                    />
                  </div>

                  <GekkoButton
                      @stop="projectRowClicked"
                      icon="fa-solid fa-trash-can"
                      class="project-actions text-danger"
                      variant="link"
                      v-gk-confirm="{
                                            title: $gettext('Delete project?'),
                                            message: $gettext('Are you sure you want to delete this project?'),
                                            options: {
                                                variant: 'danger',
                                                confirmText: $gettext('Delete')
                                            },
                                            callback: deleteProject,
                                            callbackParams: data.item.id
                                        }"
                  />
                </div>
              </template>

            </b-table>

            <div v-if="!row.item.projects.length" class="no-el-found-txt" v-translate>There are no projects assigned to this contact.</div>

          </div>
        </template>

        <template slot="bottom-row" v-if="nextUrl">
          <td :colspan="contactTableFields.length" class="table-load-more">
            <GekkoButton
                v-if="nextUrl && !fetchingContacts"
                @click="handleScroll()"
                id="load_more_button"
                class="text-info"
                variant="link"
                :text="$gettext('Load more contacts')"
            />
            <LoadingSpinner v-if="fetchingContacts" />
          </td>
        </template>

      </b-table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import FilterBar from "@/components/list_filters/FilterBar";
import ContactModal from "@/components/contacts/ContactModal";
import ProjectModal from "@/components/contacts/ProjectModal";
import CustomerService from "@/store/services/customer.service"; // TODO: rename to ContactsService
import ProjectService from "@/store/services/project.service";
import GekkoButton from "@/components/GekkoButton";
import ProjectArchive from "@/components/contacts/ProjectArchive";
import EmptyState from "@/components/ui/EmptyState"
import TotalAmountContact from "@/components/contacts/TotalAmountContact";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
  name: "ContactTable",
  components: { FilterBar, ContactModal, ProjectModal, GekkoButton, ProjectArchive, EmptyState, TotalAmountContact, LoadingSpinner },
  data() {
    return {
      loading: true,
      filters: {},
      contactTableFields: [
        {key: "name",label: this.$gettext("Name"), tdClass: "name-table-field"},
        {key: "details",label: this.$gettext("Details"), tdClass: "details-table-field"},
        {key: "full_address",label: this.$gettext("Address"), tdClass: "address-table-field"},
        {key: "label",label: this.$gettext("Type"), thClass: "text-center", tdClass: "label-table-field"}
      ],
      projectTableFields: [
        {key: "name"},
        {key: "actions"}
      ],
      contactTableItems: [],
      nextUrl: false,
      fetchingContacts: false,
      contactTableKey: 0,
      projectTableKey: 0
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId'
    }),
  },
  created() {
    window.addEventListener('scroll', this.handleScroll);
  },
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    getContacts(filters) {
      this.loading = true
      this.fetchingContacts = true

      CustomerService.listPaginated(filters).then((response) => {
        this.contactTableItems = response.data.results;
        this.contactTableItems.forEach(item => {
          item.label = this.labelText(item);
          item.full_address = this.getFullAddress(item);
          item.projectsArrowState = false; // closed
          if (!item.email.length)
            item.email = "-";
          if (!item.phone_number.length)
            item.phone_number = "-";
        });
        this.nextUrl = response.data.next
        this.loading = false
        this.fetchingContacts = false
      });
    },
    filtersChanged(filters) {
      filters.extended = 'True';
      filters.owner_company = this.getCompanyId;
      filters.search_by_name = filters.search;
      this.filters = filters;
      this.getContacts(filters);
    },
    onContactCreated(val) {
      val.projects = [];

      val = this.setMissingFields(val);

      if ((
          (this.filters.contact_type === "customer" && val.is_customer) // If filters "customer" and new contact "customer"
          || (this.filters.contact_type === "supplier" && val.is_supplier) // If filters "supplier" and new contact "supplier"
          || (this.filters.contact_type === "all") // If filters is "all"
          || (!this.filters.contact_type) // If filters not set
      ) && (
          (this.filters.is_archived === "False") // If filters is set to archived, the new contact won't be added to the list
          || (!this.filters.is_archived)) // If filters not set
      ) {
        this.contactTableItems.unshift(val); // The contact is added on top of the list
      }

      this.$store.commit("filterStore/SET_UPDATE_FILTER_CONTACTS");
    },
    onContactUpdated(val) {
      for (var i in this.contactTableItems) {
        if (this.contactTableItems[i].id === val.id) {
          val.projects = this.contactTableItems[i].projects;

          val = this.setMissingFields(val);

          this.contactTableItems[i] = val;
          break;
        }
      }

      if (this.filters.contact_type !== "all") {

        this.contactTableItems.map(c => {
          if (c.id === val.id) {
            if (this.filters.contact_type === "supplier" && !c.is_supplier) {
              this.removeContactFromList(c.id);
            }
            if (this.filters.contact_type === "customer" && !c.is_customer) {
              this.removeContactFromList(c.id);
            }
            if ((this.filters.is_archived === "False" && c.is_archived) || (this.filters.is_archived === "True" && !c.is_archived)) {
              this.removeContactFromList(c.id);
            }
          }
        })
      }
      this.contactTableKey++;
    },
    removeContactFromList(contactId) {
      this.$store.commit("filterStore/SET_UPDATE_FILTER_CONTACTS");
      this.contactTableKey++;
      this.contactTableItems = this.contactTableItems.filter(contact => contact.id !== contactId);
    },
    onProjectCreated(val) {
      this.contactTableItems.forEach(contact => {
        if (contact.id === val.company) {
          contact.projects.unshift(val);
          this.projectTableKey++;
        }
      });
    },
    onProjectUpdated(val) {
      this.projectTableKey++;

      for (var i in this.contactTableItems) {
        if (this.contactTableItems[i].id === val.company) {
          for (var j in this.contactTableItems[i].projects) {
            if (this.contactTableItems[i].projects[j].id === val.id) {
              this.contactTableItems[i].projects[j] = val;
              break;
            }
          }
          break;
        }
      }
    },
    removeProjectsFromList(val) {
      this.projectTableKey++;

      this.contactTableItems.forEach(contact => {
        contact.projects.forEach(project => {
          if (project.id === val) {
            contact.projects = contact.projects.filter(p => p.id !== val);
          }
        });
      });
    },
    contactRowClicked(item) {
      this.$router.push({name: 'EditContact', params: {contactId: item.id}});
    },
    projectRowClicked(item) {
      this.$router.push({name: 'EditProject', params: {contactId: item.company,projectId: item.id}});
    },
    goToAddProject(row) {
      this.$router.push({name: "AddProject", params: {contactId: row.item.id}});
    },
    deleteProject(id) {
      let vm = this;

      ProjectService.destroy(id).then(function() {
        vm.$store.commit('setAlert', {
          title: vm.$gettext("Project deleted"),
          text: vm.$gettext("The project was deleted correctly."),
          variant: "success",
          duration: 4
        });
        vm.removeProjectsFromList(id);
      }).catch(function () {
        vm.$store.commit('setAlert', {
          title: vm.$gettext("Error deleting contact"),
          text: vm.$gettext("This project cannot be deleted, only archived. Only projects that have no invoices or costs attached to them can be deleted."),
          variant: "danger",
          duration: 4
        });
      });

    },
    favoriteIcon(item) {
      if (item.favorite)
        return "fa-solid fa-star";
      else
        return "fa-regular fa-star";
    },
    markAsFavorite(item) {
      let vm = this;
      this.projectTableKey++;
      item.favorite = !item.favorite;
      ProjectService.update(item.id, item).catch(function(error){
        item.favorite = !item.favorite;

        let firstDictKey = Object.keys(error.data)[0];
        let msg = error.data[firstDictKey];
        vm.$store.commit('setAlert', {
          title: vm.$gettext("Error updating project"),
          text: msg,
          variant: "danger",
          duration: 4
        });
      });
    },
    labelText(item) {
      if (item.id == this.getCompanyId)
        return this.$gettext("Your company");
      if (item.is_customer && item.is_supplier)
        return this.$gettext("Sup/Cus");
      else if (item.is_customer)
        return this.$gettext("Customer");
      else if (item.is_supplier)
        return this.$gettext("Supplier");
      else
        return null;
    },
    getFullAddress(contact) {
      let fullAddress = "";

      if (contact.address)
        fullAddress = fullAddress + contact.address;

      if (contact.postal_code){
        if (fullAddress.length)
          fullAddress = fullAddress + ", ";
        fullAddress = fullAddress + contact.postal_code;
      }

      if (contact.city) {
        if (fullAddress.length)
          fullAddress = fullAddress + ", ";
        fullAddress = fullAddress + contact.city;
      }

      if (!fullAddress.length)
        fullAddress = "-";

      return fullAddress;
    },
    changeProjectsArrowState(row) {
      row.item.projectsArrowState = !row.item.projectsArrowState;
      row.toggleDetails();
    },
    getProjectsArrowIcon(item) {
      if (!item.projectsArrowState)
        return "fa-solid fa-caret-down";
      else
        return "fa-solid fa-caret-up";
    },
    getContactTypeClass(item) {
      if (item.is_supplier && item.is_customer)
        return "both-badge";
      else if (item.is_supplier)
        return "supplier-badge";
      else
        return "customer-badge";
    },
    isInViewport(element_id) {
      let element = document.getElementById(element_id)
      let rect = element.getBoundingClientRect();
      let html = document.documentElement;
      return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || html.clientHeight) &&
          rect.right <= (window.innerWidth || html.clientWidth)
      );
    },
    handleScroll() {
      if (!this.fetchingContacts && this.nextUrl) {
        let vm = this;
        let loadNewContacts = this.isInViewport('load_more_button');
        if (loadNewContacts) {
          this.fetchingContacts = true;

          CustomerService.listNextPage(this.nextUrl).then(function (response) {
            vm.fetchingContacts = false;
            vm.nextUrl = response.data.next;
            vm.contactTableItems = vm.contactTableItems.concat(response.data.results);
            vm.contactTableItems.forEach(item => {
              item.label = vm.labelText(item);
              item.full_address = vm.getFullAddress(item);
              item.projectsArrowState = false; // closed
              if (!item.email.length)
                item.email = "-";
            });
          });
        }
      }
    },
    setMissingFields(contact) {
      if (!contact.email.length)
        contact.email = "-";

      if (!contact.phone_number.length)
        contact.phone_number = "-";

      contact.label = this.labelText(contact);
      contact.full_address = this.getFullAddress(contact);

      return contact;
    }
  }
}
</script>

<style scoped lang="scss">
.av-name-cont {
  display: flex;
  flex-direction: row;
}

.avatar-col {
  min-width: 50px;
  align-self: center;
}
.avatar-color{
  background-color: #aaa;
}

.avatar-name-container {
  min-width: 200px;
}

.project-table {
  margin-left: 19px;
  border-left: 2px solid rgba(0, 0, 0, 0.15);
  width: 100%;
}

.secondary-text {
  color: #999;
}

.new-project-button {
  display: inline-block;
  padding-left: 35px;
}

.contact-type-cell {
  text-align: center;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 1rem;
}

.project-actions {
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;
}

.no-el-found-txt {
  color: #808080;
  padding-left: 35px;
}

</style>

<style lang="scss">
.contacts-table-wrap {
  .content--inner {
    min-height: 360px;
  }
}
.text-ellipsis {
  &:before {
    visibility: hidden;
    height: 0px;
  }
  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.font-16{
  font-size: 16px;
}
.name-table-field {
  min-width: 200px;
  max-width: 500px;
  width: 1%;
}

.details-table-field {
  min-width: 75px;
  width: 25%;
}

.address-table-field {
  min-width: 200px;
  width: 25%;
}

.label-table-field {
  min-width: 138px;
  width: 10%;
}

.project-row-class {
  background-color: rgba(0, 0, 0, 0) !important;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1) !important;
  }
}

@media (max-width: 576px) {
  .name-table-field {
    width: 30%;
    max-width: 200px;
  }
}
</style>
